// Customer Credit Dashboard — admin-only, org-wide aggregate view of the
// store-credit ("buy now pay later") system across ALL customers. Wired to
// ONE combined backend query (getCustomerCreditDashboardDataAction ->
// getCustomerCreditDashboard) following the same pattern as
// DashboardScreen.tsx / CustomerDashboardScreen.tsx / VendorDashboardScreen.tsx.
//
// This is deliberately a NEW, standalone screen — CustomerDashboardScreen.tsx
// explicitly drops credit data "by design" (no aggregate query existed to back
// it); that exclusion stands, this is not a tab bolted onto that screen.
//
// No date-range picker — this dashboard always shows all-time data. The KPI
// cards, Customer Credit Summary table, Customers Near Credit Limit and Top
// Customers by Outstanding are "as of today" (they read live customerDetail
// columns); Total Credit Used / Total Settled Amount, the trend charts and
// Recent Credit Transactions are all-time aggregates, bucketed by month for
// the trend charts.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import {
  Users, Wallet, CreditCard, TrendingUp, AlertTriangle, HandCoins, UserX,
} from "lucide-react";
import AppConstant from "../../../Globals/AppConstants";
import { getCustomerCreditDashboardDataAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { StatCard } from "../../../shared/stat-card";
import { DataTable } from "../../../shared/data-table";
import { Badge } from "ui/badge";
import { Skeleton } from "ui/skeleton";
import { ChartCard } from "./ChartCard";
import { TrendAreaChart } from "./TrendAreaChart";
import { CategoryBarChart } from "./CategoryBarChart";
import { CategoryDonutChart } from "./CategoryDonutChart";

const inr = (v: any) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);
const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);
const PAGE_SIZE = 10;

const SECONDARY_TABLE_TABS = [
  { key: "nearLimit", label: "Customers Near Credit Limit" },
  { key: "creditSummary", label: "Customer Credit Summary" },
  { key: "recentTransactions", label: "Recent Credit Transactions" },
] as const;
type SecondaryTableTabKey = (typeof SECONDARY_TABLE_TABS)[number]["key"];

// Friendly labels for the ledger's actual transaction-type reference rows
// (Usage/Settlement/Sales Return/Opening Balance, seeded per
// CustomerCreditService.ts's CREDIT_TRANSACTION_TYPE_GROUP_NAME) mapped onto
// the categories requested for this dashboard. "Opening Balance" is the
// closest existing type to "Adjustment/Reversal" — there is no separate
// adjustment/reversal transaction type in the schema today.
const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  "Usage": "Credit Purchase",
  "Settlement": "Settlement",
  "Sales Return": "Sales Return",
  "Opening Balance": "Adjustment/Reversal",
};
const transactionTypeLabel = (typeName: any, direction: any) =>
  TRANSACTION_TYPE_LABELS[typeName] || (direction === "CREDIT" ? "Settlement" : "Credit Purchase");

const CustomerCreditDashboardScreen = () => {
  const navigate = useNavigate();
  // Opens this customer's credit history (same call shape as customer.tsx).
  const customerLink = (row: any) => row?.customerDetailId ? (
    <button type="button" onClick={() => navigate(`/credit-ledger/${row.customerDetailId}`)} className="text-primary hover:underline text-left">
      {row.customerName}
    </button>
  ) : row?.customerName;
  const dispatch = useDispatch();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getCustomerCreditDashboardData, getCustomerCreditDashboardDataLoad } = apparelReducerState;

  const [page, setPage] = React.useState(1);
  const [recentPage, setRecentPage] = React.useState(1);
  const [activeSecondaryTab, setActiveSecondaryTab] = React.useState<SecondaryTableTabKey>("nearLimit");

  const fetchDashboard = React.useCallback(
    (selectedPage?: number, selectedRecentPage?: number) => {
      const p = selectedPage || 1;
      const rp = selectedRecentPage || 1;
      dispatch(
        getCustomerCreditDashboardDataAction({
          fromDate: "",
          toDate: "",
          limit: PAGE_SIZE,
          offset: (p - 1) * PAGE_SIZE,
          recentLimit: PAGE_SIZE,
          recentOffset: (rp - 1) * PAGE_SIZE,
        })
      );
    },
    [dispatch]
  );

  React.useEffect(() => {
    fetchDashboard(page, recentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchDashboard(nextPage, recentPage);
  };

  const onRecentPageChange = (nextPage: number) => {
    setRecentPage(nextPage);
    fetchDashboard(page, nextPage);
  };

  const data = getCustomerCreditDashboardData?.getCustomerCreditDashboard;
  const summary = data?.summary;
  const loading = getCustomerCreditDashboardDataLoad && !summary;
  const chartsLoading = getCustomerCreditDashboardDataLoad && !data;

  const topCustomers = (data?.topCustomersByOutstanding || []).map((c: any) => ({
    label: c.customerName,
    value: Number(c.outstandingBalance) || 0,
  }));

  const utilizationData = [
    { category: "Used", amount: Number(summary?.totalOutstandingCredit) || 0 },
    { category: "Available", amount: Number(summary?.totalAvailableCredit) || 0 },
  ];

  const usedTrend = (data?.trend || []).map((t: any) => ({ date: t.date, value: Number(t.used) || 0 }));
  const settledTrend = (data?.trend || []).map((t: any) => ({ date: t.date, value: Number(t.settled) || 0 }));

  const customersNearLimit = data?.customersNearLimit || [];
  const recentTransactions = data?.recentTransactions?.rows || [];
  const recentTransactionsPage = data?.recentTransactions?.page;

  const creditSummaryRows = data?.customerCreditSummary?.rows || [];
  const creditSummaryPage = data?.customerCreditSummary?.page;

  const creditSummaryColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "customerName", header: "Customer", cell: ({ row }) => customerLink(row.original) },
      { id: "creditLimit", header: "Credit Limit", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.creditLimit)}</span> },
      { id: "usedCredit", header: "Used Credit", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.usedCredit)}</span> },
      { id: "availableCredit", header: "Available Credit", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.availableCredit)}</span> },
      { id: "outstanding", header: "Outstanding", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.outstanding)}</span> },
    ],
    [navigate]
  );

  const nearLimitColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "customerName", header: "Customer", cell: ({ row }) => customerLink(row.original) },
      { id: "creditLimit", header: "Credit Limit", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.creditLimit)}</span> },
      { id: "outstanding", header: "Outstanding", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.outstanding)}</span> },
      {
        id: "utilizationPct",
        header: "Utilization",
        cell: ({ row }) => {
          const pct = Number(row.original?.utilizationPct) || 0;
          return <Badge variant={pct >= 100 ? "destructive" : "warning"}>{pct.toFixed(1)}%</Badge>;
        },
      },
    ],
    [navigate]
  );

  const recentTransactionsColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "customerName", header: "Customer", cell: ({ row }) => customerLink(row.original) },
      {
        id: "type",
        header: "Type",
        cell: ({ row }) => {
          const label = transactionTypeLabel(row.original?.transactionTypeName, row.original?.direction);
          return <Badge variant={row.original?.direction === "CREDIT" ? "success" : "warning"}>{label}</Badge>;
        },
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.amount)}</span>,
      },
      {
        id: "transactionDate",
        header: "Date",
        cell: ({ row }) => (row.original?.transactionDate ? moment(row.original.transactionDate).format("DD-MMM-YYYY") : "-"),
      },
      { id: "remarks", header: "Remarks", cell: ({ row }) => row.original?.remarks || "-" },
    ],
    [navigate]
  );

  const nearLimitCsvRows = React.useMemo(
    () => customersNearLimit.map((r: any) => ({ ...r, utilizationPct_fmt: `${(Number(r.utilizationPct) || 0).toFixed(1)}%` })),
    [customersNearLimit]
  );
  const recentTransactionsCsvRows = React.useMemo(
    () => recentTransactions.map((r: any) => ({
      ...r,
      type_label: transactionTypeLabel(r.transactionTypeName, r.direction),
      transactionDate_fmt: r.transactionDate ? moment(r.transactionDate).format("DD-MMM-YYYY") : "-",
      amount: Math.round(Number(r.amount) || 0),
    })),
    [recentTransactions]
  );

  const activeSecondaryTableProps: Record<SecondaryTableTabKey, { columns: ColumnDef<any, any>[]; data: any[]; pagination?: any }> = {
    nearLimit: { columns: nearLimitColumns, data: customersNearLimit },
    creditSummary: {
      columns: creditSummaryColumns,
      data: creditSummaryRows,
      pagination: {
        page: creditSummaryPage?.currentPage || page,
        total: creditSummaryPage?.totalCount || 0,
        pageSize: PAGE_SIZE,
        onChange: onPageChange,
      },
    },
    recentTransactions: {
      columns: recentTransactionsColumns,
      data: recentTransactions,
      pagination: {
        page: recentTransactionsPage?.currentPage || recentPage,
        total: recentTransactionsPage?.totalCount || 0,
        pageSize: PAGE_SIZE,
        onChange: onRecentPageChange,
      },
    },
  };

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="4" />

      <div className="mb-4 flex items-center gap-1 border-b">
        <button type="button" className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground">
          {AppConstant.menus.customerCreditDashboard}
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Showing all-time data.</p>
          <p className="text-xs text-muted-foreground">As of {moment(data?.asOfDate || new Date()).format("DD-MMM-YYYY")}</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {loading ? (
          Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)
        ) : (
          <>
            <StatCard label="Total Credit Limit" value={inr(summary?.totalCreditLimit)} icon={CreditCard} />
            <StatCard label="Total Credit Used" value={inr(summary?.totalCreditUsed)} icon={TrendingUp} />
            <StatCard label="Total Available Credit" value={inr(summary?.totalAvailableCredit)} icon={Wallet} />
            <StatCard label="Total Outstanding Credit" value={inr(summary?.totalOutstandingCredit)} icon={AlertTriangle} />
            <StatCard label="Total Settled Amount" value={inr(summary?.totalSettledAmount)} icon={HandCoins} />
            <StatCard label="Customers with Active Credit" value={num(summary?.customersWithActiveCredit)} icon={Users} />
            <StatCard label="Customers with Outstanding Credit" value={num(summary?.customersWithOutstandingCredit)} icon={UserX} />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Credit Utilization — Used vs Available" loading={chartsLoading} isEmpty={!summary?.totalCreditLimit}>
          <CategoryDonutChart data={utilizationData} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Top Customers by Outstanding" loading={chartsLoading} isEmpty={!topCustomers.length}>
          <CategoryBarChart data={topCustomers} valueFormatter={inr} layout="vertical" />
        </ChartCard>
        <ChartCard title="Credit Used Trend (Monthly)" loading={chartsLoading} isEmpty={!usedTrend.length}>
          <TrendAreaChart data={usedTrend} valueFormatter={inr} granularity="month" />
        </ChartCard>
        <ChartCard title="Settled Amount Trend (Monthly)" loading={chartsLoading} isEmpty={!settledTrend.length}>
          <TrendAreaChart data={settledTrend} valueFormatter={inr} granularity="month" />
        </ChartCard>
      </div>

      {/* Customers Near Credit Limit / Customer Credit Summary / Recent
          Credit Transactions — tabbed, same pattern as
          VendorDebtDashboardScreen.tsx's table tabs. */}
      <div className="mb-2 mt-6 flex flex-wrap gap-1 border-b">
        {SECONDARY_TABLE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveSecondaryTab(tab.key)}
            className={
              activeSecondaryTab === tab.key
                ? "border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
                : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      <DataTable
        columns={activeSecondaryTableProps[activeSecondaryTab].columns}
        data={activeSecondaryTableProps[activeSecondaryTab].data}
        loading={chartsLoading}
        csvExport={
          activeSecondaryTab === "nearLimit"
            ? { fileName: `customers-near-credit-limit_${moment().format("YYYY-MM-DD")}`, rows: nearLimitCsvRows, columns: [{ key: "customerName", header: "Customer" }, { key: "creditLimit", header: "Credit Limit" }, { key: "outstanding", header: "Outstanding" }, { key: "utilizationPct_fmt", header: "Utilization" }] }
            : activeSecondaryTab === "creditSummary"
            ? { fileName: `customer-credit-summary_${moment().format("YYYY-MM-DD")}`, rows: creditSummaryRows, columns: [{ key: "customerName", header: "Customer" }, { key: "creditLimit", header: "Credit Limit" }, { key: "usedCredit", header: "Used Credit" }, { key: "availableCredit", header: "Available Credit" }, { key: "outstanding", header: "Outstanding" }] }
            : { fileName: `recent-credit-transactions_${moment().format("YYYY-MM-DD")}`, rows: recentTransactionsCsvRows, columns: [{ key: "customerName", header: "Customer" }, { key: "type_label", header: "Type" }, { key: "amount", header: "Amount" }, { key: "transactionDate_fmt", header: "Date" }, { key: "remarks", header: "Remarks" }] }
        }
        pagination={activeSecondaryTableProps[activeSecondaryTab].pagination}
      />
    </div>
  );
};

export default CustomerCreditDashboardScreen;
